// The chat CONVERSATION region — the transcript + the honest status/interrupt controls + the composer,
// rendered with the adopted HEADLESS view primitives (BUILD-DEC-097, reactivating BUILD-DEC-002) and
// styled entirely in Ritona's own vocabulary (.chat-body / .bubble me|them / .chat-in .box + the
// chat-thread.css markdown typography — tokens only, G-181). The library owns presentation ONLY: the
// turns come from the `useChat` controller (the resolved `getRunTranscript` projection + the human's own
// preserved input, G-160), the run state is read, never set (N-CHAT-2 AC-1), and no turn is fabricated
// (P-SELF). An agent turn renders as MARKDOWN (formatted answer, not a wall of text); the human's turn
// stays verbatim plain text. No library identifier reaches surface copy (G-197 / N-CHAT-3 AC-2).
//
// Kept separate from ChatDock so each file stays under the size ceiling (G-085) and both the compact
// panel and the full page render the SAME conversation (one delivery path — AR-MOD-4).

import { memo, useCallback, useRef } from 'react';
import {
  AssistantRuntimeProvider,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
  useMessagePartText,
} from '@assistant-ui/react';
import type { ReasoningMessagePartProps } from '@assistant-ui/react';
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown';
import remarkGfm from 'remark-gfm';
import { Icon } from './Icon.tsx';
import { ActivityPartGroup, ActivityToolPart, ThoughtText } from './ChatActivity.tsx';
import { InboundEmailCard, ReferencedEmailCard } from './ChatEmailCard.tsx';
import { ChatPlan } from './ChatPlan.tsx';
import { useChatDraft, useComposerReturnFocus } from './chat-composer-state.ts';
import { useRitonaChatRuntime } from './chat-runtime.ts';
import { parseInboundEmailTurn } from '../surfaces/chat-email.ts';
import type { InboundEmailTurn } from '../surfaces/chat-email.ts';
import type { ChatController } from '../surfaces/useChat.ts';
import { ThinkingOrb, prefersReducedMotion } from './ThinkingOrb.tsx';

/** The calm streaming affordance appended to the in-progress agent turn: a small caret that blinks while a
 *  preview assembles, and holds STATIC under reduced motion (behaviour asserted via matchMedia, G-180). */
export function StreamingCaret(): React.JSX.Element {
  const reduce = prefersReducedMotion();
  return (
    <span
      aria-hidden="true"
      data-testid="streaming-caret"
      className="ritona-caret ml-0.5 inline-block h-4 w-0.5 translate-y-0.5 bg-text align-baseline"
      style={reduce ? { animationName: 'none' } : undefined}
    />
  );
}

/** The human's turn body — the typed input VERBATIM (plain text, whitespace preserved; never markdown —
 *  the human's own words are not reformatted, G-160). */
function UserText(): React.JSX.Element {
  const { text } = useMessagePartText();
  return <span className="chat-plain">{text}</span>;
}

/** The agent's turn body — guard-cleared text rendered as formatted markdown (headings, lists, code,
 *  tables via GFM), with the calm caret while the preview is still assembling. The text itself comes
 *  EXACTLY off the projection/stream; formatting changes presentation, never content (N-CHAT-3 AC-1). */
const AssistantText = memo(function AssistantText(): React.JSX.Element {
  const { status } = useMessagePartText();
  return (
    <div className="chat-md">
      {/* smooth typing is OFF: the preview renders EXACTLY the streamed bytes the moment they arrive —
          no display-time interpolation between real tokens (P-SELF honest preview; snapshot wins). */}
      <MarkdownTextPrimitive remarkPlugins={[remarkGfm]} smooth={false} />
      {status.type === 'running' ? <StreamingCaret /> : null}
    </div>
  );
});

/** The ONE live reasoning sentence — the ticker the owner asked to "pop as the agent is thinking" (11 Aug
 *  2026). Fixed run phases replace the placeholder in place; when the provider's sanitised one-line summary
 *  arrives it replaces those phases and remains until durable reconciliation, where the whole run's thinking
 *  folds into the collapsed disclosure instead of stacking as rows. Its markdown emphasis is RENDERED here
 *  ({@link ThoughtText}), so a summary's own step title no longer arrives as a pair of literal asterisks. */
function LiveReasoning({ text, status }: ReasoningMessagePartProps): React.JSX.Element {
  return (
    <div className="chat-tr-row chat-tr-think" data-chat-part="reasoning">
      <span className="tr-ico" aria-hidden="true">
        <Icon name="chat" size={12} />
      </span>
      <span className="tr-label">Reasoning</span>
      <span className="tr-think ritona-voice">
        <ThoughtText label={text} />
      </span>
      {status.type === 'running' ? <StreamingCaret /> : null}
    </div>
  );
}

/**
 * An inbound EMAIL that woke this run, when the turn is one (BUILD-DEC-155). An email reply is the trigger,
 * so it arrives as an ordinary human/teammate turn whose text `comms` authored: "<Name> sent this to you by
 * email:" then the person's own words. Rendered as prose it was a wall of quoted email pretending someone
 * typed it — so the envelope is recognised here and the turn renders as a card in the email thread instead.
 *
 * The recogniser reads the message's OWN text through the view runtime; nothing is added to it. A turn that
 * is not the platform envelope returns null and falls through to the ordinary bubble, which is the honest
 * default: the surface never decorates a turn it cannot account for (P-SELF).
 */
function useInboundEmailSource(): {
  readonly messageId: string | null;
  readonly legacy: InboundEmailTurn | null;
} {
  const message = useMessage();
  const source = message.metadata.custom['sourceRef'];
  if (typeof source === 'object' && source !== null) {
    const candidate = source as Readonly<Record<string, unknown>>;
    if (
      candidate['type'] === 'comms_message' &&
      typeof candidate['messageId'] === 'string' &&
      candidate['messageId'].length > 0
    ) {
      return { messageId: candidate['messageId'], legacy: null };
    }
  }
  const text = message.content
    .filter((part): part is Extract<typeof part, { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('');
  return {
    messageId: null,
    legacy:
      message.metadata.custom['legacyInboundMessage'] === true ? parseInboundEmailTurn(text) : null,
  };
}

/** One human bubble (solid Forest, right-aligned) — the prototype's own vocabulary. */
function UserMessage(): React.JSX.Element {
  const source = useInboundEmailSource();
  if (source.messageId !== null) {
    return (
      <MessagePrimitive.Root className="chat-email-turn" data-author="human">
        <ReferencedEmailCard messageId={source.messageId} />
      </MessagePrimitive.Root>
    );
  }
  // An email the reader themself sent in is still an EMAIL, not something they typed here: it takes the card,
  // not the "me" bubble, so the conversation reads as the thread of comings and goings it actually is.
  if (source.legacy !== null) {
    return (
      <MessagePrimitive.Root className="chat-email-turn" data-author="human">
        <InboundEmailCard email={source.legacy} />
      </MessagePrimitive.Root>
    );
  }
  return (
    <MessagePrimitive.Root className="bubble me">
      <MessagePrimitive.Parts components={{ Text: UserText }} />
    </MessagePrimitive.Root>
  );
}

/** One TEAMMATE bubble — a human turn on a run the reader did not start, which since the Schedule read
 *  widened (owner ruling 2026-08-02) can sit inside a routine's record: a colleague's confirmed reply ran
 *  on the routine's Session. It is left-aligned and labelled, so it can be mistaken for neither the
 *  reader's own message nor Ritona's answer. NO name is rendered: the transcript projection carries no
 *  identity, and an invented one would be worse than none (P-SELF). Verbatim plain text like any human
 *  turn — a teammate's words are not reformatted either (G-160). */
function TeammateMessage(): React.JSX.Element {
  // A teammate's turn that IS an inbound email carries the sender's NAME on the platform's own provenance
  // line, so the card can say who wrote where the bubble could only say "someone else" (see §b of the
  // BUILD-DEC-155 defects: the name was always there, it was the raw id that reached the screen).
  const source = useInboundEmailSource();
  if (source.messageId !== null) {
    return (
      <MessagePrimitive.Root className="chat-email-turn" data-author="teammate">
        <ReferencedEmailCard messageId={source.messageId} />
      </MessagePrimitive.Root>
    );
  }
  if (source.legacy !== null) {
    return (
      <MessagePrimitive.Root className="chat-email-turn" data-author="teammate">
        <InboundEmailCard email={source.legacy} />
      </MessagePrimitive.Root>
    );
  }
  return (
    <MessagePrimitive.Root className="bubble teammate" data-author="teammate">
      <span className="bubble-from">Someone else on your team</span>
      <MessagePrimitive.Parts components={{ Text: UserText }} />
    </MessagePrimitive.Root>
  );
}

/** One agent bubble (sunken) — its run's sanitised trace first, then the markdown answer. No internal
 *  actor is rendered (G-197); the trace rows are visually distinct from the answer (DS-CHAT-01). */
function AssistantMessage(): React.JSX.Element {
  return (
    <MessagePrimitive.Root className="bubble them">
      <MessagePrimitive.Parts
        components={{
          Text: AssistantText,
          Reasoning: LiveReasoning,
          tools: { Fallback: ActivityToolPart },
          ToolGroup: ActivityPartGroup,
          ReasoningGroup: ActivityPartGroup,
        }}
        unstable_showEmptyOnNonTextEnd={false}
      />
    </MessagePrimitive.Root>
  );
}

/** The honest empty state — the conversation has no turn yet. Never fabricates a greeting turn (P-SELF). */
function EmptyConversation(): React.JSX.Element {
  return (
    <p className="cb-text" style={{ color: 'var(--text-muted)' }}>
      Ask Ritona anything, or start a piece of work. Every message routes to the agent and stays on
      the record; the dashboard adds no reasoning and sends no screen context.
    </p>
  );
}

/** The THINKING affordance: shown for the ENTIRE active run, including after structured status,
 *  activity, or answer-preview events arrive. The server publishes its first safe reasoning status almost
 *  immediately, so hiding this row on the first event made the animation imperceptible in production. It is
 *  a labelled status affordance, NOT a transcript turn — no agent turn is fabricated (P-SELF). */
function ThinkingRow({ chat }: { readonly chat: ChatController }): React.JSX.Element | null {
  const thinking = chat.sending || chat.streaming || chat.runState === 'running';
  if (!thinking) return null;
  return (
    <div role="status" data-testid="chat-thinking" className="chat-thinking">
      <ThinkingOrb />
      <span>Ritona is thinking…</span>
      {chat.activeRunId !== null ? (
        <button
          type="button"
          onClick={() => void chat.interrupt()}
          className="btn quiet sm chat-thinking-stop"
        >
          Stop
        </button>
      ) : null}
    </div>
  );
}

/** Terminal run state only. The active run has exactly ONE affordance: ThinkingRow, which the composer
 *  region below carries in place of its input while the run is in flight. */
function RunStatus({ chat }: { readonly chat: ChatController }): React.JSX.Element | null {
  if (chat.runState === 'interrupted') {
    // A RESTART is not a human stop. Saying "You stopped Ritona" for both told the owner he had stopped
    // work he never touched, when a deploy landed mid-run (3 Aug 2026) — and sent him looking for a
    // mistake he had not made. The record always knew which it was; the screen did not.
    const stoppedByRestart = chat.interruptedBy === 'restart';
    const pausedForInactivity = chat.interruptedBy === 'inactivity';
    const pauseWindow = chat.inactivityTimeoutSeconds;
    return (
      <div className="chat-status" {...(pausedForInactivity ? { 'data-run-paused': true } : {})}>
        <span className="lbl">
          {pausedForInactivity
            ? pauseWindow === undefined
              ? 'Paused after no new progress. Completed actions are on the record.'
              : `Paused after ${pauseWindow} seconds without new progress. Completed actions are on the record.`
            : stoppedByRestart
              ? 'Ritona restarted before finishing this. Ask again and it will pick it up.'
              : 'You stopped Ritona. It is on the record.'}
        </span>
        {pausedForInactivity ? (
          <button
            type="button"
            className="btn quiet sm"
            disabled={chat.sending}
            onClick={() => void chat.continueRun()}
          >
            Continue
          </button>
        ) : null}
      </div>
    );
  }
  // A FAILED run is surfaced honestly, never swallowed (deep-sweep 2026-07-09 F2: a spawned run that
  // failed — cost cap, tool error, provider outage — previously rendered nothing at all). The message is
  // generic-honest (the typed reason stays on the owned record; no internal detail on a surface — G-197).
  if (chat.runState === 'failed') {
    return (
      <div role="status" className="chat-status" data-run-failed>
        <span className="lbl">
          <span className="dot" style={{ background: 'var(--clay)' }} aria-hidden="true" />
          This run failed — nothing was actioned. Your message is on the record; try again, or check
          Monitoring for the run.
        </span>
      </div>
    );
  }
  return null;
}

/** An honest, sanitised note (a refused/queued send) — never an internal error/stack (G-197). */
function ChatNote({ note }: { readonly note: string | null }): React.JSX.Element | null {
  if (note === null) return null;
  return (
    <div role="status" className="chat-note">
      {note}
    </div>
  );
}

/** The IDLE state of the composer region (prototype .chat-in .box) — a text input + the send control. The
 *  run guards that used to disable this button live in the STATE now: mid-run this form is not on the
 *  screen at all, so the only honest reason left to refuse a send is an empty message. */
function ComposerForm({
  draft,
  setDraft,
  onSubmit,
  inputRef,
}: {
  readonly draft: string;
  readonly setDraft: (value: string) => void;
  readonly onSubmit: (event: React.FormEvent) => void;
  readonly inputRef: React.RefObject<HTMLInputElement | null>;
}): React.JSX.Element {
  return (
    <form onSubmit={onSubmit} className="chat-in">
      <div className="box">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          aria-label="Message Ritona"
          placeholder="Message Ritona…"
        />
        <button
          type="submit"
          aria-label="Send message"
          disabled={draft.trim().length === 0}
          className="send"
        >
          <Icon name="send" size={13} />
        </button>
      </div>
    </form>
  );
}

/**
 * The composer REGION — ONE control with two states, never a second box (owner, 11 Aug 2026: the separate
 * "Ritona is thinking…" pill stacked above the dock was "an extra box which isn't necessary", and the
 * composer beneath it was unusable during a run anyway). Idle it is the input + Send; mid-run the SAME
 * footprint carries `ThinkingRow` — relocated whole, so whatever that row says now or gains later lands
 * here untouched — with its Stop standing where Send stands. Stop is not rewired: it is the same
 * `chat.interrupt` inside the same row, so "You stopped Ritona" keeps meaning what it meant.
 *
 * The swap reads ThinkingRow's OWN condition, not the composer's narrower one (they differed by
 * `chat.sending`): the narrower one left a beat right after Send where the input still looked live.
 *
 * Sending stays BESPOKE and SYNCHRONOUS: a submit calls `chat.send` directly so the human's own turn is
 * preserved in the same commit (G-160 immediate, no deferred hop through the view runtime). It sends ONLY
 * the typed message (N-CHAT-1 AC-4); the view library renders the thread, never the transport.
 */
function Composer({ chat }: { readonly chat: ChatController }): React.JSX.Element {
  const [draft, setDraft] = useChatDraft();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const send = chat.send;
  // One visible conversation turn at a time. A queued follow-up has no queue→run stream handoff yet, so
  // accepting it here would execute an invisible, non-stoppable run after the current one completes.
  const thinking = chat.sending || chat.streaming || chat.runState === 'running';
  useComposerReturnFocus(thinking, inputRef);
  const onSubmit = useCallback(
    (event: React.FormEvent): void => {
      event.preventDefault();
      if (thinking) return;
      const text = draft;
      setDraft('');
      void send(text);
    },
    [draft, setDraft, thinking, send],
  );
  if (thinking) {
    return (
      <div className="chat-in chat-in-run">
        <ThinkingRow chat={chat} />
      </div>
    );
  }
  return <ComposerForm draft={draft} setDraft={setDraft} onSubmit={onSubmit} inputRef={inputRef} />;
}

/** The full conversation region: transcript viewport, honest run status + note, and the TWO-STATE composer
 *  (input + Send when idle; the thinking row + Stop while a run is in flight). Exactly three dock rows at
 *  any moment — plan, status/note, composer — never a fourth (owner, 11 Aug 2026). */
export function ChatConversation({ chat }: { readonly chat: ChatController }): React.JSX.Element {
  const runtime = useRitonaChatRuntime(chat);
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root className="chat-main-inner chat-thread">
        <ThreadPrimitive.Viewport
          aria-label="Conversation transcript"
          role="log"
          className="chat-body"
        >
          <ThreadPrimitive.Empty>
            <EmptyConversation />
          </ThreadPrimitive.Empty>
          {/* Three renderers, three authors. `SystemMessage` is the view library's third message seat and
              carries the TEAMMATE turns the server attributed away from the reader — without it a
              colleague's words would fall back to a bubble that claims someone else wrote them. */}
          <ThreadPrimitive.Messages
            components={{ UserMessage, AssistantMessage, SystemMessage: TeammateMessage }}
          />
        </ThreadPrimitive.Viewport>
        {/* The agent's current plan / to-do list, pinned above the status bar so it stays visible and
            updates as the agent works (BUILD-DEC-099). Renders nothing when there is no plan. */}
        <ChatPlan plan={chat.plan} />
        <RunStatus chat={chat} />
        <ChatNote note={chat.note} />
        <Composer chat={chat} />
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  );
}
